import { generateClassName } from '@klv/shared/utils/functions';

import Icon from '@klv/atoms/icon';
import { useTranslation } from '@klv/hooks/useTranslation';
import AuthorsParentComponent from '@klv/organisms/authors';
import TranslatedDate from '@klv/atoms/translated-date';
import BlogCards from '@klv/organisms/blog-cards';
import BlogCategoryButton from '@klv/molecules/blog-category-button';
import Buttons from '@klv/molecules/buttons';
import { Breadcrumbs } from '@klv/molecules/breadcrumbs';
import { hasSideDownloadCtaData } from '@klv/molecules/side-download-cta/utils';
import { parser } from '@klaviyo-mkt/blocks/parser';

import { Wrapper } from '@klv/utils/wrapper';

import { TaxonomySubhead } from './components/taxonomy-subhead';

import BlogHeroAuthorThumbnail from './components/blog-hero-author-thumbnail';
import Sidebar from './components/sidebar';
import useBackToTop from './hooks/useBackToTop';
import useAddEvent from './hooks/useAddEvent';

import '@klaviyo-mkt/blocks/blocks.scss';
import * as styles from './styles/editorialLayout.module.scss';

import type { IEditorialLayoutProps } from './types';

const  EditorialLayout = ({
  authors,
  category,
  windowLocation,
  showBreadcrumbs,
  breadcrumbs,
  customPadding = { customPaddingTop: 'xs', customPaddingBottom: 'xs' },
  contained = true,
  format = 'rich',
  header,
  inlinePadding = true,
  isPost,
  content,
  title,
  templatesEditorial,
}: IEditorialLayoutProps) => {
  const { t } = useTranslation();

  // used for scrolling to the top of the page
  const { showBackToTopButton, blockContentRef } = useBackToTop();

  // used for replacing shortcodes with the correct 3rd party content
  useAddEvent();

  // used for checking if the post has a side download CTA
  const hasSideDownloadCta = hasSideDownloadCtaData({ 
    backgroundColor: templatesEditorial?.sideDownloadCta?.backgroundColor || 'cotton', 
    bodyCopy: templatesEditorial?.sideDownloadCta?.bodyCopy || '', 
    buttons: templatesEditorial?.sideDownloadCta?.buttons || [],
    headline: templatesEditorial?.sideDownloadCta?.headline || '',
    thumbnail: templatesEditorial?.sideDownloadCta?.thumbnail,
  });

  // parsed content
  const { h2List, parsedContent } = parser(content || '');
  const hasTocData = (h2List?.length > 0);
  const hasSidebar = format === 'rich' && (hasSideDownloadCta || (hasTocData && templatesEditorial?.showTableOfContents));

  // determine category posts structure for blog cards
  const categoryPosts = Array.isArray(category?.posts) ? category?.posts : category?.posts?.nodes || [];

  return (
    <>
      {showBreadcrumbs && (
        <Breadcrumbs breadcrumbs={breadcrumbs} className={styles.breadcrumbs} />
      )}
      <Wrapper 
      className={styles.editorialLayout}
      customPadding={customPadding}
      backgroundColor="transparent"
      fadeIn={0}
      contained={contained}
      inlinePadding={inlinePadding}
      grid={true}
      >
        {hasSidebar && (
          <Sidebar 
            hasSideDownloadCta={!!hasSideDownloadCta}
            h2List={h2List}
            showTableOfContents={templatesEditorial?.showTableOfContents}
            sideDownloadCta={templatesEditorial?.sideDownloadCta}
            windowLocation={windowLocation}
          />
        )}
        <div className={hasSidebar ? styles.contentWithSidebar : styles.contentWithoutSidebar} ref={blockContentRef}>
          <div className={styles.header}> 
            <h1 className={generateClassName(['h2', 'semiBold'])}>{title}</h1>
            {templatesEditorial?.titleCtas?.buttons && templatesEditorial.titleCtas.buttons.length > 0 && (
              <div className={styles.titleCtas}>
                <Buttons 
                  buttons={templatesEditorial.titleCtas.buttons}
                  buttonColor='dark'
                  variants={['primary', 'secondary']}
                />
              </div>
            )}
            {format === 'rich' && (
              <div className={styles.infoContainer}>
                {authors && templatesEditorial.metaHeaderCopy.displayAuthorsDate && 
                  <BlogHeroAuthorThumbnail
                    authors={authors?.map((author) => {
                      return {
                        image: author?.usersAuthor?.profilePhoto,
                        link: author?.uri,
                        name: author.name,
                      };
                    })}
                    readTime={header.readTime}
                  />
                }
                {category && (
                  <BlogCategoryButton
                    className={styles.blogCategoryButton}
                    afterText={header.displayAuthorDate && <TranslatedDate date={header.date} options={{
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    }}/>}
                    link={category.link}
                    name={category.name}
                  />
                )}
              </div>
            )}
            {format === 'plain' && (header.customers || header.industries || header.platforms) && (
              <>
                <TaxonomySubhead
                  customers={header.customers}
                  industries={header.industries}
                  platforms={header.platforms}
                />
                {header.featuredImage?.node && (
                  <figure className={generateClassName([styles.featuredImage])}>
                    {header.featuredImage.node?.sourceUrl && (
                      <img
                        alt={header.featuredImage.node?.altText}
                        sizes='(max-width: 1023px) 100vw, (max-width: 1199px) 733px, (max-width: 1365px) 822px, 792px'
                        src={header.featuredImage.node.sourceUrl}
                        srcSet={header.featuredImage.node.srcSet}
                      />
                    )}
                    {header.featuredImage.node?.caption && (
                      <figcaption
                        className={styles.featuredImageCaption}
                        dangerouslySetInnerHTML={{ __html: header.featuredImage.node?.caption }}
                      />
                    )}
                  </figure>
                )}
              </>
            )}

          </div>
          <div className='blockContent'>
            <hr className='wp-block-separator' />
            {parsedContent}
          </div>
          { /* Only used on Blog Posts */}
          {isPost && (
            <>
              {authors && templatesEditorial.metaHeaderCopy.displayAuthorsInFooter && (
                <AuthorsParentComponent authors={authors} />
              )}
              {category && templatesEditorial?.showRelatedContent && categoryPosts?.length > 0 && (
                <div className={generateClassName([styles.relatedContentGrid])}>
                  <div className={styles.relatedContent}>
                    <BlogCards
                      backgroundColor="white"
                      displayGrayLine={templatesEditorial.showRelatedContentGrayLine}
                      canShowViewAll={false}
                      headline={t('molecules_blogCard_related_content')}
                      category={{
                        name: category.name,
                        link: category.link,
                        posts: categoryPosts,
                      }}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Wrapper>
      {showBackToTopButton && (
        <div className={styles.backToTopButtonContainer}>
          <button
            onClick={() => {
              const blockContent = blockContentRef.current;
              const fixedHeaderHeight = 120;
              if (blockContent) {
                const blockContentTop = blockContent.getBoundingClientRect().top;
                const scrollValue = window.scrollY + blockContentTop - fixedHeaderHeight;
                window.scrollTo({
                  top: scrollValue,
                  behavior: 'smooth',
                });
              }
            }}
            className={styles.backToTopButton}
          >
            <Icon iconName='ArrowDown' size={1.8} fillColor='white' />
          </button>
        </div>
      )}
    </>
  );
};

export default EditorialLayout;
